import React from 'react'
import { connect } from 'react-redux'
import injectSheet from 'react-jss'
import style from './style'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBan } from '@fortawesome/free-solid-svg-icons'
import WireDistanceWarning from 'components/Wire/DistanceWarning'
import ConnectedWiringWarning from 'components/ConnectedWiringWarning'
import DropLengthInput from './DropLengthInput'
import { BorderPicker } from 'components/MarkupDetails/ColorPicker'
import Panel from 'components/Panel'
import DeviceDescription from 'components/DeviceDescription'
import VideoPanel from 'components/VideoPanel'
import { updateUiAction } from 'actions/uiActions'
import { updateNetworkClosetAction, deleteNetworkClosetAction, addWireAction, deleteWireAction, removeDeviceWiresAction } from 'actions/entityActions'
import { messageAction } from 'actions/messageActions'
import { changeCameraAction } from 'actions/cameraActions'
import { zoomToSingleCameraAction } from 'actions/mapActions'
import { updateCurrentEntityAction } from 'actions/applicationActions'
import { DEFAULT_WIRE_OPTIONS, WIRE_FOLLOWER_OPTIONS, WIRE_SNAP_ICON, WIRE_BLOCK_SNAP_ICON, CAMERA_SELECTED_COLORS } from 'helpers/globals'
import { connectedDevicesSelector, connectableDevicesSelector } from 'selectors/components'
import snapToClosestObject from 'helpers/wires/snapToClosestObject'
import getMapProjection from 'helpers/map/getMapProjection'
import sortItems from 'helpers/sortItems'
import getTypeIconString from 'helpers/wiredDevices/getTypeIconString'

const mapStateToProps = state => {
  const { entities: { networkClosets: { byId } }, currentEntity: { id }, map: { ref }, ui: { drawingWire, isImperial, cameraDescriptionPanelOpen } } = state
  const { validDevices, invalidDevices } = connectableDevicesSelector(state)
  const { connectedDevices, totalDistance } = connectedDevicesSelector(state)

  return {
    currentNetworkCloset: byId[id],
    map: ref,
    devices: connectedDevices,
    totalDistance,
    validDevices,
    invalidDevices,
    drawingWire,
    isImperial,
    cameraDescriptionPanelOpen
  }
}

class NetworkClosetDetails extends React.Component {
  constructor(props) {
    super(props)
    this.markerDragListeners = []
    this.mapContainer = null
    this.moveListener = null
    this.clickListener = null
    this.downListener = null
    this.skipEventListener = null
    this.dragListener = null
    this.warningTimer = null
    this.markups = []
    this.state = { dragging: false }
  }

  cancel = _ => {
    const { mapContainer, moveListener, clickListener, downListener, skipEventListener, dragListener, warningTimer, markups } = this

    if (!this.props.drawingWire) return

    mapContainer.removeEventListener('mousemove', moveListener)
    mapContainer.removeEventListener('click', clickListener, true)
    mapContainer.removeEventListener('mousedown', downListener, true)
    mapContainer.removeEventListener('touchstart', downListener, true)
    mapContainer.removeEventListener('rightclick', skipEventListener, true)
    mapContainer.removeEventListener('contextmenu', skipEventListener, true)
    google.maps.event.removeListener(dragListener)
    clearTimeout(warningTimer)
    markups.forEach(m => m.setMap(null))
    this.props.map.setOptions({ draggableCursor: '' })
    this.props.updateUiAction({ drawingWire: false })
  }

  activateWireInsertMode = _ => {
    const { map, drawingWire, currentNetworkCloset, validDevices, invalidDevices, updateUiAction, addWireAction } = this.props

    if (drawingWire) return

    const mouseFollower = new google.maps.Marker({
      icon: { ...WIRE_SNAP_ICON, path: google.maps.SymbolPath.CIRCLE },
      cursor: "url(\"https://maps.google.com/mapfiles/kml/shapes/cross-hairs_highlight.png\") 16 16, crosshair",
      position: currentNetworkCloset.location,
      clickable: false,
      zIndex: 110,
      visible: false,
      map
    })

    let path = [currentNetworkCloset.location]
    const wire = new google.maps.Polyline({
      ...DEFAULT_WIRE_OPTIONS,
      strokeColor: CAMERA_SELECTED_COLORS['blue'],
      path,
      map
    })

    let followerPath = [currentNetworkCloset.location, currentNetworkCloset.location]
    const followerWire = new google.maps.Polyline({
      ...WIRE_FOLLOWER_OPTIONS,
      strokeColor: CAMERA_SELECTED_COLORS['blue'],
      path: followerPath,
      visible: false,
      map
    })

    const connectedWarning = new ConnectedWiringWarning({ map, visible: false, ignoreClick: _ => skipClick = true })

    let snappedDevice, snappedInvalidDevice
    let followersDisplayed = false

    map.setOptions({ draggableCursor: "url(\"https://maps.google.com/mapfiles/kml/shapes/cross-hairs_highlight.png\") 16 16, crosshair" })

    this.mapContainer = document.getElementById('mapContainer')
    const mapComponentRef = this.mapContainer.children[0]
    const zoomButton = document.querySelector('.gm-bundled-control')
    const projection = getMapProjection(map)

    const complete = device => {
      this.cancel()
      addWireAction(wire, { type: device.type, id: device.id }, { type: 'networkCloset', id: currentNetworkCloset.id })
    }

    const setIconColor = (color, changeIcon) => {
      let icon = mouseFollower.getIcon()

      if (changeIcon) {
        if (color === 'red') icon = { ...WIRE_BLOCK_SNAP_ICON, path: faBan.icon[4], anchor: new google.maps.Point(faBan.icon[0] / 2, faBan.icon[1] / 2) }
        else icon = { ...WIRE_SNAP_ICON, path: google.maps.SymbolPath.CIRCLE }
      }

      icon.strokeColor = CAMERA_SELECTED_COLORS[color]
      icon.fillColor = CAMERA_SELECTED_COLORS[color]

      mouseFollower.setIcon(icon)
    }

    let moveListenerFrame
    const handleMove = e => {
      const bounds = this.mapContainer.getBoundingClientRect()
      const relativePixel = { x: e.clientX - bounds.left, y: e.clientY - bounds.top }
      let snapResult = snapToClosestObject(relativePixel, projection, validDevices)

      mouseFollower.setPosition(snapResult.position)
      if (path.length > 0) {
        followerPath[1] = snapResult.position
        followerWire.setPath(followerPath)
      }

      // valid target
      if (snapResult.snappedObject) {
        if (!snappedDevice) setIconColor('green', snappedInvalidDevice)

        connectedWarning.setVisible(false)
        snappedInvalidDevice = null
        return snappedDevice = snapResult.snappedObject
      }

      // check if there is an invalid target
      snapResult = snapToClosestObject(relativePixel, projection, invalidDevices)
      if (snapResult.snappedObject) {
        const newInvalidDevice = snapResult.snappedObject

        if (!snappedInvalidDevice) setIconColor('red', true)
        if (newInvalidDevice !== snappedInvalidDevice) {
          const text = newInvalidDevice.type === 'nonEthernet' ? `${newInvalidDevice.name} is not an Ethernet device` : `${newInvalidDevice.name} already cabled to ${newInvalidDevice.networkClosetName}`
          connectedWarning.setPosition(snapResult.position, text)
        }

        snappedDevice = null
        return snappedInvalidDevice = newInvalidDevice
      }

      // no snap targets
      if (snappedDevice || snappedInvalidDevice) setIconColor('blue', snappedInvalidDevice)
      snappedDevice = null
      snappedInvalidDevice = null
      connectedWarning.setVisible(false)

      if (!followersDisplayed) {
        followersDisplayed = true
        mouseFollower.setVisible(true)
        followerWire.setVisible(true)
      }
    }

    this.moveListener = e => {
      if (moveListenerFrame) cancelAnimationFrame(moveListenerFrame)

      moveListenerFrame = requestAnimationFrame(_ => {
        handleMove(e)
        moveListenerFrame = null
      })
    }

    this.clickListener = e => {
      if (!mapComponentRef.contains(e.target)) return e.stopPropagation()
      if (skipClick || zoomButton && zoomButton.contains(e.target)) return

      const bounds = this.mapContainer.getBoundingClientRect()
      const relativePixel = { x: e.clientX - bounds.left, y: e.clientY - bounds.top }
      e.stopPropagation()
      let snapResult = snapToClosestObject(relativePixel, projection, validDevices)

      // valid target
      if (snapResult.snappedObject) {
        path.push(snapResult.position)
        wire.setPath(path)
        const strokeColor = snapResult.snappedObject.type === 'camera' ? CAMERA_SELECTED_COLORS[snapResult.snappedObject.color] : snapResult.snappedObject.color
        wire.setOptions({ path: path.slice().reverse(), strokeColor })

        return complete(snapResult.snappedObject)
      }

      // check if there is an invalid target
      snapResult = snapToClosestObject(relativePixel, projection, invalidDevices)
      if (snapResult.snappedObject) {
        const text = newInvalidDevice.type === 'nonEthernet'
                      ? `${snapResult.snappedObject.name} is not an Ethernet device`
                      : `${snapResult.snappedObject.name} already cabled to ${snapResult.snappedObject.networkClosetName}`
        connectedWarning.setPosition(snapResult.position, text)

        clearTimeout(this.warningTimer)

        return this.warningTimer = setTimeout(_ => connectedWarning.getVisible() && connectedWarning.setVisible(false), 2000)
      }

      // no targets, add point
      path.push(snapResult.position)
      wire.setPath(path)
      mouseFollower.setPosition(snapResult.position)
      setIconColor('blue', true)
      followerPath = [snapResult.position, snapResult.position]
      followerWire.setPath(followerPath)

      if (connectedWarning.getVisible()) connectedWarning.setVisible(false)

      if (!followersDisplayed) {
        followersDisplayed = true
        mouseFollower.setVisible(true)
        followerWire.setVisible(true)
      }
    }

    let skipClick = false
    this.downListener = e => {
      if (!mapComponentRef.contains(e.target)) return
      skipClick = false
    }

    this.skipEventListener = e => e.stopPropagation()

    this.mapContainer.addEventListener('mousemove', this.moveListener)
    this.mapContainer.addEventListener('click', this.clickListener, true)
    this.mapContainer.addEventListener('mousedown', this.downListener, true)
    this.mapContainer.addEventListener('touchstart', this.downListener, true)
    this.mapContainer.addEventListener('rightclick', this.skipEventListener, true)
    this.mapContainer.addEventListener('contextmenu', this.skipEventListener, true)
    this.dragListener = google.maps.event.addListener(map, 'dragstart', _ => skipClick = true)
    this.markups = [mouseFollower, followerWire, wire, connectedWarning]

    updateUiAction({ drawingWire: true })
  }

  componentDidMount = _ => {
    const { currentNetworkCloset: { fillMarker } } = this.props
    if (fillMarker) this.addDragListeners(fillMarker)
  }

  componentDidUpdate = prevProps => {
    const { currentNetworkCloset: { fillMarker } } = this.props
    if (!prevProps.currentNetworkCloset.fillMarker && fillMarker) this.addDragListeners(fillMarker)
  }

  addDragListeners = marker => {
    this.markerDragListeners.push(google.maps.event.addListener(marker, 'dragstart', _ => this.setState({ dragging: true })))
    this.markerDragListeners.push(google.maps.event.addListener(marker, 'dragend', _ => this.setState({ dragging: false })))
  }

  componentWillUnmount = _ => {
    this.markerDragListeners.forEach(listener => listener && listener.remove())
    this.cancel()
  }

  wiringInstructions = _ => (
    <div className={this.props.classes.instructions}>
      <FontAwesomeIcon icon='info-circle' />
      <div>
        To cable a device in:
        <ol>
          <li>Click "Draw cabling to Device"</li>
          <li>Click an unconnected device to complete</li>
          <li>Adjust the cable as necessary</li>
        </ol>
      </div>
    </div>
  )

  deleteWireListener = device => _ => {
    const { currentNetworkCloset: { locked, name }, deleteWireAction } = this.props

    if (locked) {
      if (!window.confirm(`${name} is locked, are you sure you want to delete this cable?`)) return
    } else if (device.locked) {
      if (!window.confirm(`${device.name} is locked, are you sure you want to delete this cable?`)) return
    }

    deleteWireAction(device.wire.id)
  }

  cameraListener = camera => {
    const { updateCurrentEntityAction, changeCameraAction, zoomToSingleCameraAction } = this.props

    return _ => {
      camera.active ? updateCurrentEntityAction({ id: camera.id, type: 'camera' }) : changeCameraAction(camera.id)
      zoomToSingleCameraAction(camera)
    }
  }

  otherListener = device => {
    const { updateCurrentEntityAction, map } = this.props

    return _ => {
      updateCurrentEntityAction({ id: device.id, type: device.objType })
      map.panTo(device.location)
    }
  }

  deviceIcon = device => {
    if (device.objType === 'camera') return <FontAwesomeIcon icon={['fac', 'camera-box']} style={{ color: CAMERA_SELECTED_COLORS[device.color], marginRight: '1rem', fontSize: '1.4rem' }} />

    return (
      <div
        className={this.props.classes.otherIcon}
        style={{ background: device.color }}
      >
        <FontAwesomeIcon
          icon={device.objType === 'wiredDevice' ? getTypeIconString(device.type) : ['far', 'microchip']}
          style={{ margin: 'auto' }}
        />
      </div>
    )
  }

  deviceRow = device => {
    const { props: { isImperial, classes }, state: { dragging }, cameraListener, otherListener, deleteWireListener } = this

    return (
      <div key={device.id + device.objType} className={classes.device}>
        { this.deviceIcon(device) }
        <div style={{ flex: 1 }}>
          <a onClick={device.objType === 'camera' ? cameraListener(device) : otherListener(device)}>
            {device.name} <FontAwesomeIcon icon='arrow-right' />
          </a>
          <span className={classes.distanceText}>
            Length:
            <span className={dragging ? classes.distanceTextFadeOut : classes.distanceTextFadeIn}>
              { ` ${device.distance}` }
              { device.showWarning && <WireDistanceWarning isImperial={isImperial} /> }
            </span>
          </span>
        </div>
        <a
          onClick={deleteWireListener(device)}
          className={classes.disconnect}
        >
          <FontAwesomeIcon icon='times-circle' />
          Disconnect
        </a>
      </div>
    )
  }

  renderWiringButton = _ => {
    const { activateWireInsertMode, props: { drawingWire, validDevices, classes } } = this

    if (drawingWire) {
      return (
        <div
          onClick={this.cancel}
          className={classes.cancelButton}
        >
          Cancel cabling
        </div>
      )
    }

    if (validDevices.length === 0) {
      return (
        <div className={classes.disabledButton}>
          All Devices Cabled
        </div>
      )
    }

    return (
      <div
        onClick={activateWireInsertMode}
        className={classes.drawWireButton}
      >
        Draw cabling to Device
      </div>
    )
  }

  render() {
    const { currentNetworkCloset, currentNetworkCloset: { id, name, locked, location, color }, map, totalDistance, devices, validDevices,
      cameraDescriptionPanelOpen, updateUiAction, updateNetworkClosetAction, deleteNetworkClosetAction, removeDeviceWiresAction, classes } = this.props

    return (
      <div className={classes.details}>
        <div className={classes.topUiControls}>
          <a onClick={_ => updateUiAction({ sidebarView: 'cameraList' })}>
            <FontAwesomeIcon icon='caret-left' className={classes.goBackButton} />
            All Project Items
          </a>
          <a
            onClick={_ => {
              if (locked) {
                this.props.messageAction('generalError', 'Cannot delete locked Label. Right click to unlock.')
              } else if (window.confirm(`Are you sure you want to delete ${name} and all attached cables?`)) {
                removeDeviceWiresAction(id, 'networkCloset')
                deleteNetworkClosetAction(id)
              }
            }}
            className={classes.destroyButton}>
            <FontAwesomeIcon icon={['far', 'trash-alt']} />
          </a>
        </div>
        <div className={classes.container}>
          <div title={'Name'} className={classes.label}>Name</div>
          <div className={classes.nameWrapper}>
            <input
              type='text'
              className={classes.nameInput}
              onChange={({ target: { value } }) => {
                updateNetworkClosetAction(id, { name: value })
              }}
              value={name || ''}
            />
          </div>
          <div className={classes.actions}>
            <a onClick={_ => updateNetworkClosetAction(id, { locked: !locked })}>
              <FontAwesomeIcon className={classes.lockButton} icon={locked ? 'lock' : 'unlock'} />
            </a>
            <a onClick={_ => map.panTo(location)}>
              <FontAwesomeIcon icon='map-marker-alt' className={classes.targetButton} />
            </a>
          </div>
        </div>
        <div className={classes.container}>
          <span className={classes.colorLabel}>Color</span>
          <BorderPicker
            currentMarkup={{ id, type: 'networkCloset', strokeColor: color }}
            updateMarkupAction={(id, action) => updateNetworkClosetAction(id, { color: action.strokeColor })}
            cameraOnly
          />
        </div>
        <DropLengthInput id={id} desktop containerClass={classes.container} />
        <div className={classes.container}>
          <div style={{ flex: '1 1 auto', color: 'black' }}>
            <span>Cabled Devices: {devices.length}</span>
            { devices.length > 0 &&
              <span className={classes.distanceText}>
                Total Length:
                <span className={this.state.dragging ? classes.distanceTextFadeOut : classes.distanceTextFadeIn}>{ ` ${totalDistance}` }</span>
              </span>
            }
          </div>
          { this.renderWiringButton() }
        </div>
        <div className={classes.devicesContainer + (devices.length === 0 && validDevices.length > 0 ? ' noDevices' : '')}>
          { devices.length > 0
              ? sortItems(devices).map(this.deviceRow)
              : validDevices.length > 0 && this.wiringInstructions()
          }
        </div>
        <Panel
          active={cameraDescriptionPanelOpen}
          component={<DeviceDescription currentDevice={currentNetworkCloset} type='networkCloset' />}
          title='Location, Description, Notes'
          updateUiAction={updateUiAction}
          uiKey='cameraDescriptionPanelOpen'
        />
        <VideoPanel
          videoUrl='https://www.youtube.com/embed/7IXXUpQ3zCI?rel=0&showinfo=0'
          uiKey='wiringVideoPanelOpen'
        />
      </div>
    )
  }
}

export default connect(mapStateToProps, { updateUiAction, updateNetworkClosetAction, deleteNetworkClosetAction, addWireAction, deleteWireAction, removeDeviceWiresAction,
  messageAction, changeCameraAction, zoomToSingleCameraAction, updateCurrentEntityAction })(injectSheet(style)(NetworkClosetDetails))



// WEBPACK FOOTER //
// ./src/components/NetworkClosetDetails/index.js